import { useState } from 'react';
import { TabsContent } from '@/components/ui/tabs';
import { usePermissions } from '@/hooks/use-permissions';
import { SettingsPage, SettingsTableHead, useTabParam, type SettingsTab } from '@/modules/settings';
import { EscalationLog, EscalationLogTools } from '@/modules/escalation-policy/escalation-log';
import { ESCALATION_LOG_COLUMNS } from '@/modules/escalation-policy/escalation-log-columns';
import { useEscalationLogFilters } from '@/modules/escalation-policy/use-escalation-log-filters';
import { AutomationLogsTools, SentRepliesTab } from './automation-sent-replies';
import { AUTOMATION_LOG_COLUMNS, useAutomationLogFilters } from './automation-log-filters';
import { SettingsLog, SettingsLogTools } from './settings-log';
import { SETTINGS_LOG_COLUMNS } from './settings-log-columns';
import { DeviationLog, DeviationLogTools } from './deviation-log';
import { deviationLogColumns, useDeviationLogFilters } from './deviation-log-filters';
import { ToolServerLog, ToolServerLogTools } from './tool-server-log';
import { TOOL_SERVER_LOG_COLUMNS, useToolServerLogFilters } from './tool-server-log-filters';
import type { DayRange } from '@/lib/automation-replies';

type LogsTab = 'automation' | 'escalations' | 'settings' | 'deviations' | 'tool-server';

const AUTOMATION_TAB: SettingsTab<LogsTab> = { value: 'automation', label: 'Automation' };
const ESCALATIONS_TAB: SettingsTab<LogsTab> = { value: 'escalations', label: 'Escalations' };
const SETTINGS_TAB: SettingsTab<LogsTab> = { value: 'settings', label: 'Settings' };
const DEVIATIONS_TAB: SettingsTab<LogsTab> = { value: 'deviations', label: 'Deviations' };
const TOOL_SERVER_TAB: SettingsTab<LogsTab> = { value: 'tool-server', label: 'Tool Logs' };

/**
 * Settings › Logs: what Kim did on its own, every escalation it ran, who changed the settings and, for
 * admins, how far each AI draft was from the agent's reply, and each integration action's calls on the tool
 * server. `?tab=` opens a tab directly.
 */
export default function LogsPage() {
	const { can } = usePermissions();
	const canViewBrand = can('view', 'brand');
	const tabs = [
		AUTOMATION_TAB,
		// Escalations and settings changes are brand settings; the page itself is gated on automation.
		...(canViewBrand ? [ESCALATIONS_TAB] : []),
		// Deviations read the shadow comparison, which is admin-only like the shadow report.
		...(can('view', 'reports') ? [DEVIATIONS_TAB] : []),
		// Tool server calls are read under the integrations permission, like the API.
		...(can('view', 'integrations') ? [TOOL_SERVER_TAB] : []),
		...(canViewBrand ? [SETTINGS_TAB] : [])
	];
	const [tab, setTab] = useTabParam(tabs, 'automation');
	const automationFilters = useAutomationLogFilters();
	const escalationFilters = useEscalationLogFilters();
	const [settingsDays, setSettingsDays] = useState<DayRange>({});
	const deviationFilters = useDeviationLogFilters();
	const toolServerFilters = useToolServerLogFilters();
	const header = {
		automation: { tools: <AutomationLogsTools filters={automationFilters} />, columns: AUTOMATION_LOG_COLUMNS },
		escalations: { tools: <EscalationLogTools filters={escalationFilters} />, columns: ESCALATION_LOG_COLUMNS },
		settings: { tools: <SettingsLogTools days={settingsDays} onDaysChange={setSettingsDays} />, columns: SETTINGS_LOG_COLUMNS },
		deviations: {
			tools: <DeviationLogTools filters={deviationFilters} />,
			columns: deviationLogColumns(deviationFilters.sort, deviationFilters.setSort)
		},
		'tool-server': { tools: <ToolServerLogTools filters={toolServerFilters} />, columns: TOOL_SERVER_LOG_COLUMNS }
	}[tab];

	return (
		<SettingsPage
			title='Logs'
			tabs={tabs}
			tab={tab}
			onTabChange={setTab}
			tools={header.tools}
			subHeader={<SettingsTableHead columns={header.columns} />}
		>
			<TabsContent value='automation' className='mt-0'>
				<SentRepliesTab filters={automationFilters} />
			</TabsContent>
			<TabsContent value='escalations' className='mt-0'>
				<EscalationLog filters={escalationFilters} />
			</TabsContent>
			<TabsContent value='settings' className='mt-0'>
				<SettingsLog days={settingsDays} />
			</TabsContent>
			<TabsContent value='deviations' className='mt-0'>
				<DeviationLog filters={deviationFilters} />
			</TabsContent>
			<TabsContent value='tool-server' className='mt-0'>
				<ToolServerLog filters={toolServerFilters} />
			</TabsContent>
		</SettingsPage>
	);
}
